import {Box, Flex, Text} from "@chakra-ui/react";
import * as React from "react";
import {useContext} from "react";
import {AppContext, AppContextI} from "../../components/context/ContextWrapper";
import {Spot} from "../../components/types";
import {buildFlowGraph} from "./customFlow/spotFlowGraph";
import {layoutFlow} from "../../components/flowGraph/layout";
import FlowCanvas from "../../components/flowGraph/FlowCanvas";
import FlowControls from "../../components/flowGraph/FlowControls";
import {createSpotRuleEngine} from "./customFlow/spotRuleEngine";

type Props = {
    spots: Spot[];
    expanded?: boolean;
    onToggleExpand?: () => void;
};

const MIN_LAYOUT_SCALE = 1;
const SCALE_STEP = 0.2;

const CustomFlowPanel = ({spots, expanded, onToggleExpand}: Props) => {
    const appContext = useContext<AppContextI>(AppContext);
    const selectedSpot = appContext.getSelection();
    const selectedNodeId = selectedSpot?.key || selectedSpot?.id || selectedSpot?.name;
    const [layoutScale, setLayoutScale] = React.useState<number>(MIN_LAYOUT_SCALE);
    const [verticalScale, setVerticalScale] = React.useState<number>(MIN_LAYOUT_SCALE);
    const [showImplicitPaths, setShowImplicitPaths] = React.useState<boolean>(false);
    const ruleEngine = React.useMemo(() => createSpotRuleEngine(), []);
    const {nodes, edges} = React.useMemo(() => {
        const graph = buildFlowGraph(spots);
        return layoutFlow(graph.nodes, graph.edges, {
            rankSep: 90 * layoutScale,
            nodeSep: 50 * verticalScale
        });
    }, [layoutScale, spots, verticalScale]);
    const layoutKey = React.useMemo(() => `${layoutScale}-${verticalScale}`, [layoutScale, verticalScale]);
    const handleSelectNode = React.useCallback((nodeId: string, isStart: boolean) => {
        if (isStart) {
            appContext.setSelection(undefined);
            return;
        }
        const match = spots.find((spot) => spot.key === nodeId)
            || spots.find((spot) => spot.id === nodeId)
            || spots.find((spot) => spot.name === nodeId);
        if (match) {
            appContext.setSelection(match);
        }
    }, [appContext, spots]);

    return (
        <Box
            border={"1px solid #e2e8f0"}
            borderRadius={"md"}
            padding={3}
            bg={"white"}
            boxShadow={"sm"}
            display={"flex"}
            flexDirection={"column"}
            flex={expanded ? 1 : undefined}
            minH={expanded ? 0 : undefined}
        >
            <Flex alignItems={"center"} justifyContent={"space-between"} mb={2} gap={3}>
                <Text fontSize={"sm"} fontWeight={600}>
                    Possible routes
                </Text>
            </Flex>
            <Box
                border={"1px solid #e2e8f0"}
                borderRadius={"md"}
                bg={"gray.50"}
                height={expanded ? "100%" : "360px"}
                minHeight={expanded ? 0 : "280px"}
                flex={expanded ? 1 : undefined}
                position={"relative"}
            >
                <FlowCanvas
                    nodes={nodes}
                    edges={edges}
                    layoutKey={layoutKey}
                    onSelectNode={(node) => handleSelectNode(node.id, node.kind === "start")}
                    onClearSelection={() => appContext.setSelection(undefined)}
                    showImplicitPaths={showImplicitPaths}
                    ruleEngine={ruleEngine}
                    selectedNodeId={selectedNodeId}
                />
                <FlowControls
                    isExpanded={expanded}
                    onToggleExpand={onToggleExpand}
                    onWidenX={() => setLayoutScale((prev) => +(prev + SCALE_STEP).toFixed(2))}
                    onNarrowX={() => setLayoutScale((prev) => Math.max(MIN_LAYOUT_SCALE, +(prev - SCALE_STEP).toFixed(2)))}
                    onWidenY={() => setVerticalScale((prev) => +(prev + SCALE_STEP).toFixed(2))}
                    onNarrowY={() => setVerticalScale((prev) => Math.max(MIN_LAYOUT_SCALE, +(prev - SCALE_STEP).toFixed(2)))}
                    disableNarrowX={layoutScale <= MIN_LAYOUT_SCALE}
                    disableNarrowY={verticalScale <= MIN_LAYOUT_SCALE}
                    showImplicitPaths={showImplicitPaths}
                    onToggleImplicitPaths={() => setShowImplicitPaths((prev) => !prev)}
                />
            </Box>
        </Box>
    );
};

export default CustomFlowPanel;
